import React from "react"
import styles from "./styles.module.scss"
import { PaymentMethod } from "./PaymentMethod"
import { formatCurrency } from "@cruisen/i18n"
import { QuoteCard } from "../QuoteCard"
import PlusOutline from "~icons/eva/plus-outline"
import { cardTypeTitle, TokenizedCreditCard } from "@cruisen/contract"
import EditOutline from "~icons/eva/edit-outline"
import classNames from "classnames"

interface PaymentMethodCardProps {
  paymentType: "creditCard" | "bankAccount"
  selectedPaymentMethod: string
  onPaymentTypeChange: (type: string, method: string) => void
  lineItem: {
    total: number
    deposit: number
    totalDueDate: string
  }
  existingCreditCards: TokenizedCreditCard[]
  onAddCard: () => void
  onEditCard: (cardId: string) => void
  selectedCardId: string
  onCardSelect: (cardId: string) => void
}

export const PaymentMethodCard: React.FC<PaymentMethodCardProps> = ({
  paymentType,
  selectedPaymentMethod,
  onPaymentTypeChange,
  lineItem,
  existingCreditCards,
  onAddCard,
  onEditCard,
  selectedCardId,
  onCardSelect,
}) => {
  const isCreditCardSelected = paymentType === "creditCard"

  return (
    <QuoteCard title="Payment method">
      <div className={styles.radioInput}>
        <input
          type="radio"
          checked={isCreditCardSelected}
          onChange={() => onPaymentTypeChange("creditCard", "creditCardTotalPayment")}
          id="creditCard"
        />
        <label htmlFor="creditCard">Credit card</label>
      </div>
      {isCreditCardSelected && (
        <div className={styles.methodsContainer}>
          <PaymentMethod
            title="Full amount"
            description="You pay the entire cost of the cruise up front."
            amount={formatCurrency(lineItem.total, 2)}
            isSelected={selectedPaymentMethod === "creditCardTotalPayment"}
            onPress={() => onPaymentTypeChange("creditCard", "creditCardTotalPayment")}
          />
          <PaymentMethod
            title="Deposit only"
            description="You will only pay the deposit, and the remainder of the cruise fare must be paid before the final due date."
            amount={formatCurrency(lineItem.deposit, 2)}
            isSelected={selectedPaymentMethod === "creditCardDepositPayment"}
            onPress={() => onPaymentTypeChange("creditCard", "creditCardDepositPayment")}
            paymentMethodDetails={
              <div className={styles.paymentDetails}>
                <div className={styles.paymentRow}>
                  <span>{new Date(lineItem.totalDueDate).toLocaleDateString()}</span>
                  <span>{formatCurrency(lineItem.total - lineItem.deposit, 2)}</span>
                </div>
              </div>
            }
          />

          <p className={styles.note}>
            NOTE: Your credit card won’t be charged yet. We will transfer your card details to your
            advisor, who will handle the payment for this quote.
          </p>

          <div className={styles.cardsList}>
            {existingCreditCards.length > 0 ? (
              existingCreditCards.map((card) => (
                <div key={card.id} className={styles.cardItem}>
                  <div className={styles.cardRadio}>
                    <input
                      type="radio"
                      checked={selectedCardId === String(card.id)}
                      onChange={() => onCardSelect(String(card.id))}
                      className={styles.cardInput}
                      id={`card-${card.id}`}
                    />
                    <div
                      onClick={() => onCardSelect(String(card.id))}
                      className={classNames(
                        styles.cardRadioInput,
                        selectedCardId === String(card.id) && styles.cardRadioInputChecked,
                      )}
                    >
                      {selectedCardId === String(card.id) ? (
                        <div className={styles.cardRadioInputInside} />
                      ) : null}
                    </div>
                    <label htmlFor={`card-${card.id}`} className={styles.cardDetails}>
                      <p className={styles.cardType}>
                        {cardTypeTitle(card.cardType)} ending in {card.cardLast4}
                      </p>
                      <span className={styles.expDate}>
                        Exp. date {`${card.expiryMonth}/${String(card.expiryYear).slice(-2)}`}
                      </span>
                    </label>
                  </div>
                  <div className={styles.cardInfo}>
                    <button
                      className={styles.editButton}
                      onClick={() => onEditCard(String(card.id))}
                      aria-label="Edit card"
                    >
                      <EditOutline />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className={styles.noCard}>No cards available</p>
            )}
          </div>

          <button className={styles.addCardButton} onClick={onAddCard}>
            <PlusOutline className={styles.plusIcon} />
            <p>Add card</p>
          </button>
        </div>
      )}
    </QuoteCard>
  )
}
